import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, FolderOpen, Star, Lock, Globe, ChevronRight } from 'lucide-react';
import { API } from '../App';

export default function RepoSelector({ open, onClose, onSelectRepo }) {
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) loadRepos();
  }, [open]);

  const loadRepos = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await API.get('/api/github/repos');
      setRepos(r.data.repos || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load repositories');
    }
    setLoading(false);
  };

  const filtered = repos.filter(r =>
    !search || r.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.description?.toLowerCase().includes(search.toLowerCase())
  );

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-lg bg-[#1A1A1A] border border-[#2A2A2A] max-h-[80vh] flex flex-col"
          onClick={e => e.stopPropagation()}
          data-testid="repo-selector-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#2A2A2A]">
            <h2 className="text-sm font-semibold font-['Barlow_Condensed']">Select Repository</h2>
            <button onClick={onClose} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#A0A0A0] hover:text-[#F5F5F5]">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Search */}
          <div className="p-3 border-b border-[#2A2A2A]">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search repositories..."
                className="w-full pl-9 pr-3 py-2.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00]"
                autoFocus
                data-testid="repo-search-input"
              />
            </div>
          </div>

          {/* Repo list */}
          <div className="flex-1 overflow-y-auto p-2" style={{ maxHeight: '50vh' }}>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-5 h-5 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="p-4 text-center">
                <p className="text-sm text-[#EF4444]">{error}</p>
                <button onClick={loadRepos} className="text-xs text-[#FF4D00] mt-2">Retry</button>
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-[#A0A0A0] text-center py-6">
                {search ? 'No repos match your search' : 'No repositories found'}
              </p>
            ) : (
              <div className="space-y-0.5">
                {filtered.map((repo, i) => (
                  <button
                    key={repo.full_name || i}
                    onClick={() => onSelectRepo(repo)}
                    className="w-full flex items-center gap-3 px-3 py-3 min-h-[44px] text-left hover:bg-[#2A2A2A]/40 transition-colors group"
                    data-testid={`repo-item-${i}`}
                  >
                    <FolderOpen className="w-4 h-4 text-[#FF4D00] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{repo.full_name}</span>
                        {repo.private ? (
                          <Lock className="w-3 h-3 text-[#A0A0A0] shrink-0" />
                        ) : (
                          <Globe className="w-3 h-3 text-[#A0A0A0] shrink-0" />
                        )}
                      </div>
                      {repo.description && (
                        <p className="text-[10px] text-[#A0A0A0] truncate mt-0.5">{repo.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        {repo.language && (
                          <span className="text-[9px] font-mono text-[#A0A0A0]">{repo.language}</span>
                        )}
                        {repo.stargazers_count > 0 && (
                          <span className="text-[9px] font-mono text-[#A0A0A0] flex items-center gap-0.5">
                            <Star className="w-2.5 h-2.5" /> {repo.stargazers_count}
                          </span>
                        )}
                        {repo.updated_at && (
                          <span className="text-[9px] font-mono text-[#A0A0A0]">
                            {new Date(repo.updated_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#A0A0A0] shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
